import { useEffect, useRef, useState } from "react";
import { Command } from "lucide-react";
import { useShallow } from "zustand/react/shallow";

import { useChartStore } from "@/stores/chartStore";
import { useSettingsStore } from "@/stores/settingsStore";
import { useUIStore } from "@/stores/uiStore";
import { TIMEZONE_OPTIONS } from "@/lib/constants/timezones";

function formatTime(tz: string): string {
  return new Intl.DateTimeFormat(undefined, {
    timeZone: tz,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  }).format(new Date());
}

function getTzAbbrev(tz: string): string {
  try {
    const parts = new Intl.DateTimeFormat(undefined, {
      timeZone: tz,
      timeZoneName: "short",
    }).formatToParts(new Date());
    return parts.find((p) => p.type === "timeZoneName")?.value ?? tz;
  } catch {
    return tz;
  }
}

function useClock(timezone: string) {
  const [time, setTime] = useState(() => formatTime(timezone));

  useEffect(() => {
    setTime(formatTime(timezone));
    const id = setInterval(() => setTime(formatTime(timezone)), 1000);
    return () => clearInterval(id);
  }, [timezone]);

  return time;
}

export function StatusBar() {
  const openCommandPalette = useUIStore((s) => s.openCommandPalette);
  const { symbol, hasData, oldestEpoch, latestEpoch, isLoadingHistory } = useChartStore(
    useShallow((state) => ({
      symbol: state.symbol,
      hasData: state.hasData,
      oldestEpoch: state.oldestEpoch,
      latestEpoch: state.latestEpoch,
      isLoadingHistory: state.isLoadingHistory,
    })),
  );
  const timezone = useSettingsStore((s) => s.chartAppearance.timezone) || "UTC";
  const setTimezone = useSettingsStore((s) => s.setTimezone);

  const time = useClock(timezone);
  const abbrev = getTzAbbrev(timezone);

  const [tzOpen, setTzOpen] = useState(false);
  const tzPopoverRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!tzOpen) return;
    const handler = (e: MouseEvent) => {
      if (tzPopoverRef.current && !tzPopoverRef.current.contains(e.target as Node)) {
        setTzOpen(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [tzOpen]);

  const hairline: React.CSSProperties = {
    color: "var(--lyt-border-default)",
    fontSize: "var(--text-xs)",
    fontFamily: "var(--font-ui)",
  };

  const formatEpoch = (ms: number | null) => {
    if (!ms) return "—";
    return new Intl.DateTimeFormat(undefined, {
      timeZone: timezone,
      year: "numeric",
      month: "short",
      day: "numeric",
    }).format(new Date(ms));
  };

  return (
    <footer
      className="hidden items-center justify-between px-4 md:flex"
      style={{
        position: "absolute",
        bottom: 0,
        left: 0,
        right: 0,
        height: "var(--height-status)",
        backgroundColor: "color-mix(in srgb, var(--lyt-bg-surface) 85%, transparent)",
        backdropFilter: "blur(8px)",
        WebkitBackdropFilter: "blur(8px)",
        borderTop: "1px solid var(--lyt-border-subtle)",
        zIndex: "var(--z-overlay-panel)",
      }}
      aria-label="Workspace status"
    >
      {/* Left cluster — loaded file info */}
      <div className="flex min-w-0 items-center gap-3">
        {isLoadingHistory ? (
          <span style={{ color: "var(--lyt-text-secondary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-ui)" }}>
            Loading {symbol}…
          </span>
        ) : hasData ? (
          <>
            <span style={{ color: "var(--lyt-text-secondary)", fontSize: "var(--text-xs)", fontFamily: "var(--font-ui)" }}>
              {symbol}
            </span>
            {oldestEpoch && latestEpoch && (
              <>
                <span style={hairline}>|</span>
                <span style={{ color: "var(--lyt-text-muted)", fontSize: "var(--text-xs)", fontFamily: "var(--font-mono)", fontVariantNumeric: "tabular-nums" }}>
                  {formatEpoch(oldestEpoch)} – {formatEpoch(latestEpoch)}
                </span>
              </>
            )}
          </>
        ) : (
          <span style={{ color: "var(--lyt-text-muted)", fontSize: "var(--text-xs)", fontFamily: "var(--font-ui)" }}>
            Drop a CSV file to begin
          </span>
        )}
      </div>

      {/* Right cluster — clock + Ctrl+K */}
      <div className="flex items-center gap-3">
        <div className="relative" ref={tzPopoverRef}>
          <button
            type="button"
            onClick={() => setTzOpen((v) => !v)}
            aria-label="Change timezone"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: "var(--mono-sm)",
              fontVariantNumeric: "tabular-nums",
              letterSpacing: "0.02em",
              color: tzOpen ? "var(--lyt-accent)" : "var(--lyt-text-muted)",
              cursor: "pointer",
              background: "none",
              border: "none",
              padding: "2px 6px",
              borderRadius: 4,
              transition: "color 0.15s",
            }}
            className="hover:bg-surface-hover"
          >
            {time} {abbrev}
          </button>

          {tzOpen && (
            <div
              style={{
                position: "absolute",
                bottom: "calc(100% + 8px)",
                right: 0,
                zIndex: "var(--z-overlay-panel)",
                backgroundColor: "var(--lyt-bg-elevated)",
                border: "1px solid var(--lyt-border-subtle)",
                borderRadius: 10,
                padding: "10px",
                minWidth: 230,
                boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
              }}
            >
              <p
                style={{
                  fontSize: "var(--text-xs)",
                  fontFamily: "var(--font-ui)",
                  color: "var(--lyt-text-muted)",
                  marginBottom: 8,
                  paddingLeft: 2,
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                }}
              >
                Timezone
              </p>
              <select
                value={timezone}
                onChange={(e) => {
                  setTimezone(e.target.value);
                  setTzOpen(false);
                }}
                style={{
                  width: "100%",
                  background: "var(--lyt-bg-surface)",
                  border: "1px solid var(--lyt-border-subtle)",
                  borderRadius: 6,
                  color: "var(--lyt-text-primary)",
                  fontSize: "var(--text-sm)",
                  padding: "6px 8px",
                }}
              >
                {TIMEZONE_OPTIONS.map((tz) => (
                  <option key={tz.iana} value={tz.iana}>
                    {tz.label}
                  </option>
                ))}
              </select>
            </div>
          )}
        </div>

        <span style={hairline}>|</span>

        <button
          type="button"
          id="onboarding-search"
          onClick={openCommandPalette}
          className="interactive"
          title="Commands · Ctrl+K"
          aria-label="Open command palette"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: "var(--lyt-text-muted)",
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: "0 4px",
            borderRadius: 4,
          }}
        >
          <Command size={13} strokeWidth={1.75} />
        </button>
      </div>
    </footer>
  );
}
